<template>
  <EditionModal :is-displayed="id !== null" :query-param="ongoingGwQueryParam" class="gw-ongoing">
    <FiltersZone no-options-button>
      <div slot="shortcuts" class="v-centerer">
        <div class="gw-duration">
          {{ currentDisplayTime }}
        </div>

        <UsersSync :entity-id="id || 0" entity-type="gw-ongoing" />
      </div>
    </FiltersZone>

    <div v-if="compGembawalk" class="f-h-below-filters gw-ongoing__content-wrapper">
      <div class="gw-ongoing__content">
        <div class="gw-ongoing__content__steps">
          <h2 class="basic-sub-title">
            {{ $t("message.GW_STEPS") }}
          </h2>
          <div v-if="isSmLandscape" class="sm-steps-list v-centerer">
            <div
              v-for="(step, stepIndex) in compGembawalk.steps"
              :key="stepIndex"
              class="h-v-centerer generic-border generic-border--text-btn"
              :class="{
                'generic-border--focused': stepIndex == currentStepIndex,
                'generic-border--passed': stepIndex < currentStepIndex,
              }"
              @click="refreshStepData(stepIndex, currentStepIndex)"
            >
              {{ stepIndex + 1 }}
            </div>
          </div>
          <GwStepsNavigator
            v-else
            :gw="compGembawalk"
            :index="currentStepIndex"
            @stepChange="refreshStepData"
          />
        </div>
        <div class="f-h gw-ongoing__content__current">
          <h2 class="basic-sub-title gw-ongoing__content__current__title">
            {{ currentStep.name }}
          </h2>

          <div class="gw-ongoing__content__current__sep" />

          <div class="gw-ongoing__content__current__wrapper generic-border">
            <div class="legend legend--bottom-margin">
              {{ $t('message.GW_GOAL') }}
            </div>
            <EditableLabel v-model="currentStep.objective" disabled :placeholder="$t('message.GW_GOAL')" />

            <div class="gw-ongoing__content__current__wrapper__sep" />

            <div ref="checklistsScroller" class="gw-ongoing__content__current__wrapper__scrollable">
              <GwChecklistsWrapper
                :gw="compGembawalk"
                :step-index="currentStepIndex"
                :check-lists="currentStep.checklists"
                :focusable="true"
                @edit="checklistEdited"
                @focusChange="checklistsFocusChange"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="gw-ongoing__ok-step v-centerer">
        <div class="gw-ongoing__ok-step__btns v-centerer">
          <div class="generic-border generic-border--text-btn generic-border--gray-border" @click="okStep(2)">
            <span v-if="$store.state.device.isSmartphonePortrait">
              {{ $t('message.NOT_APPLICABLE_SHORT') }}
            </span>
            <span v-else>
              {{ $t('message.GW_NOT_APPLICABLE') }}
            </span>
          </div>
          <div class="generic-border generic-border--text-btn generic-border--red" @click="okStep(0)">
            <Icon>{{ close }}</Icon>
            <span v-if="!$store.state.device.isSmartphonePortrait">{{ $t('message.GW_INVALIDATE_STEP') }}</span>
          </div>
          <div class="generic-border generic-border--text-btn generic-border--green" @click="okStep(1)">
            <Icon>{{ tickExtraBold }}</Icon>
            <span v-if="!$store.state.device.isSmartphonePortrait">{{ $t('message.GW_VALIDATE_STEP') }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Send messages to audited teams -->
    <transition name="fade" mode="out-in">
      <div v-show="ending" class="dark-overlay v-centerer" @click="hideEndingBox(true)">
        <div class="dark-overlay-content large gw-team-msgs" @click.stop="">
          <CloseButton @click.native="hideEndingBox(true)" />
          <h2 class="basic-sub-title">
            {{ `${pageTitle}${$t("message.TWO_DOTS")}${$t("message.GW_IS_COMPLETE")}` }}
          </h2>
          <div class="f-w f-h-below-subtitle content-gradient">
            <div class="f-h">
              <!-- eslint-disable-next-line vue/no-v-html -->
              <div class="field-hint" v-html="$t('message.GW_TEAM_MSG_HINT')" />
              <div class="option-link" @click="toggleTeamMsgs">
                <span v-if="allInfoTeamsUnticked">{{ $t("message.SELECT_ALL") }}</span>
                <span v-else>{{ $t("message.UNSELECT_ALL") }}</span>
              </div>
              <div class="sep" />
              <div v-if="teamMessages.length === 0">
                {{ $t('message.GW_TEAM_MSG_NONE') }}
              </div>
              <div
                v-for="(teamMsg, msgIndex) in teamMessages"
                :key="teamMsg.teamId"
                class="gw-team-msg"
                :class="{ mb15: msgIndex == teamMessages.length - 1 }"
              >
                <h3 class="mt10">
                  {{ gwMessageSend(teamMsg.teamName) }}
                </h3>
                <Checkbox v-model="teamMsg.sendMsg" :title="$t('message.GW_TEAM_MSG_DISPLAY')" />
                <!-- eslint-disable-next-line vue/no-v-html -->
                <div class="f-w main-msg" :class="{ 'greyed-out': !teamMsg.sendMsg }" v-html="teamMsg.msg" />
                <Checkbox v-model="teamMsg.sendCustomMsg" :title="$t('message.GW_TEAM_MSG_DISPLAY_CUSTOM')" />
                <textarea
                  v-model="teamMsg.customMsg"
                  rows="1"
                  class="f-w bordered-field"
                  :disabled="!teamMsg.sendCustomMsg"
                  @input="textareaAutoSize($event)"
                />
                <div v-if="msgIndex < teamMessages.length - 1" class="sep" />
              </div>
            </div>
          </div>
          <button
            class="generic-border generic-border--text-btn generic-border--green gw-team-msgs__send"
            @click="end"
          >
            {{ $t("message.GW_END_GW") }}
          </button>
        </div>
      </div>
    </transition>
    <!-- Audited team s data -->
    <!-- WARNING: hiding this as there's no solution yet as to how this will be implemented with Akiani design -->
    <LeftDrawerPanel
      v-if="false"
      :right="true"
      class="gw-team-data"
      expander="/images/teams-gray.png"
      :no-rotation="true"
    >
      <h2 class="basic-sub-title">
        {{ $t("message.GW_TEAM_LINKED_TO_STEP") }}
      </h2>
      <h3>{{ decodeHTML(teamName) }}</h3>
      <button class="add-button blue mt10 small-icon" @click="problemsExpanded = !problemsExpanded">
        <img
          class="f-h"
          src="/images/next-blue.png"
          alt=""
          :style="fetchProblemsArrow"
        >
        {{ $t("message.GW_FETCH_PROBLEMS") }}
      </button>
      <TransitionExpand>
        <div v-if="problemsExpanded">
          <div v-if="teamProblems == null">
            {{ $t("message.LOADING") }}
          </div>
          <div v-else>
            <ProblemItem
              v-for="problem in teamProblems"
              :key="problem.id"
              :problem="problem"
              min
            />
          </div>
        </div>
      </TransitionExpand>
      <button class="add-button blue mt10 small-icon" @click="actionsExpanded = !actionsExpanded">
        <img
          class="f-h"
          src="/images/next-blue.png"
          alt=""
          :style="fetchActionsArrow"
        >
        {{ $t("message.GW_FETCH_ACTIONS") }}
      </button>
      <TransitionExpand>
        <div v-if="actionsExpanded">
          <div v-if="teamActions == null">
            {{ $t("message.LOADING") }}
          </div>
          <div v-else>
            <PlanActionItem
              v-for="action in teamActions"
              :key="action.id"
              :action="action"
              :all-users="teamActionUsers"
              :dashboard="true"
            />
          </div>
        </div>
      </TransitionExpand>
    </LeftDrawerPanel>
    <!-- Files display -->
    <FileViewer
      v-if="currentItem && fileViewerVisible"
      v-model="currentItem.files"
      pathname="gwClItems/"
      @close="fileViewerVisible = false"
    />
    <!-- Intrusive help for covid version -->
    <IntrusiveHelp
      :displayable="!!currentTeam.covidVersion && !!compGembawalk.id"
      help-key="GwOngoing"
      @triggerComponentHelp="triggerComponentHelp"
    />
  </EditionModal>
</template>

<script>
/**
 * Page used for an ongoing field tour. Allow the user to carry out the field tour.
 *
 * TODO: displaying a problem sheet over the field tour and refreshing the page
 * TODO: to be designed: indicator field
 * TODO: to be designed: Nb OK / NOK in steps list
 * TODO: to be designed: Team current problems and actions
 * TODO: to be designed: Field tour's end modal
 */

import $ from 'jquery';
import { cloneDeep } from 'lodash';
// Import components
import Checkbox from '@/components/checkbox.vue';
import CloseButton from '@/components/close-button.vue';
import EditableLabel from '@/components/editable-label.vue';
import EditionModal from '@/components/edition-modals/edition-modal.vue';
import FileViewer from '@/components/file-system/file-viewer.vue';
import FiltersZone from '@/components/filters-zone.vue';
import GwChecklistsWrapper from '@/components/gw-checklists-wrapper.vue';
import GwStepsNavigator from '@/components/gw-steps-navigator.vue';
import Icon from '@/components/icon.vue';
import IntrusiveHelp from '@/components/intrusive-help.vue';
import LeftDrawerPanel from '@/components/left-drawer-panel.vue';
import PlanActionItem from '@/components/plan-action-item.vue';
import ProblemItem from '@/components/problem-item.vue';
import TransitionExpand from '@/components/transition-expand.vue';
import UsersSync from '@/components/users-sync.vue';
// Import mixins
import mixinSocketHandler from '@/mixins/socket-handler.vue';
// Import utils
import IAHelp from '@/utils/classes/IAHelp';
import IAHelpStep from '@/utils/classes/IAHelpStep';
import SocketMessages from '@/utils/classes/SocketMessages';
import {
  calert,
  decodeHTML,
  detectEscapeKey,
  undetectEscapeKey,
  secondsToArray,
} from '@/utils/main';
import { globalEventDispatcher, ShizenGlobalEvent } from '@/utils/event-dispatcher/global';
import { close, tickExtraBold } from '@/assets/icons/';
import { FETCH_ONGOING_GW, FETCH_GW, VALIDATE_STEP } from '@/store/modules/gemba-walks/action-types';
import { SET_GW } from '@/store/modules/gemba-walks/mutation-types';
import { ongoingGwQueryParam } from '@/utils/navigation-constants';

function gwOngoingInitialData() {
  return {
    ongoingGwQueryParam,
    close,
    tickExtraBold,
    stepScrollPosition: [],
    gembaWalk: {},
    currentStepIndex: 0,
    stepIndexBeforeHelp: 0,
    secondsFromStart: 0,
    stepTimer: null,
    lastStepStart: null,
    lastStepInitialDuration: 0,
    /** Focusing checklists items */
    focusedCl: 0,
    focusedClItem: 0,
    fileViewerVisible: false,
    /** GW end popup */
    ending: false,
    teamMessages: [],
    teamMsgsUnselectAll: false,
    stopping: false,
    /** Audited team's data */
    problemsExpanded: false,
    actionsExpanded: false,
    teamProblems: null,
    teamActions: null,
    teamActionUsers: [],
    destroyed: false,
  };
}

export default {

  name: 'GwOngoingModal',

  components: {
    Checkbox,
    CloseButton,
    EditableLabel,
    EditionModal,
    FileViewer,
    FiltersZone,
    GwChecklistsWrapper,
    GwStepsNavigator,
    Icon,
    IntrusiveHelp,
    LeftDrawerPanel,
    PlanActionItem,
    ProblemItem,
    TransitionExpand,
    UsersSync,
  },

  mixins: [mixinSocketHandler],

  data() {
    return {
      ...gwOngoingInitialData(),
      // Properties used by other components. See src/views/readme.md
      pageBackButtonAction: () => {
        this.cancel();
      },
    };
  },

  computed: {
    /** Number: ID of the problem. If 0, creating a new problem */
    id() {
      return this.$route.query[ongoingGwQueryParam] === undefined
        ? null
        : Number(this.$route.query[ongoingGwQueryParam]);
    },
    /** Object: GW Object from the state */
    stateGembaWalk() {
      return this.$store.state.gembaWalks.gwPerId[`${this.id}`];
    },
    defaultDisplay() {
      if (this.isSmLandscape) {
        return [true, false];
      }
      if (typeof this.currentTeam.gwHidePreviousData === 'undefined') {
        return null;
      }
      return [true, this.currentTeam.gwHidePreviousData !== 1];
    },
    currentStep() {
      const defaultStep = {
        id: 0,
        prevId: 0,
        name: '',
        prevComments: [],
        checklists: [],
        comments: [],
      };
      if (this.compGembawalk && this.compGembawalk.steps && this.compGembawalk.steps.length) {
        return this.compGembawalk.steps[this.currentStepIndex] || defaultStep;
      }
      return defaultStep;
    },
    currentDisplayTime() {
      const time = secondsToArray(this.secondsFromStart);
      return `${(time[0] === '00' ? '' : `${time[0]}:`) + time[1]}:${time[2]}`;
    },
    fetchProblemsArrow() {
      return this.problemsExpanded ? 'transform: rotate(90deg)' : '';
    },
    fetchActionsArrow() {
      return this.actionsExpanded ? 'transform: rotate(90deg)' : '';
    },
    teamName() {
      return this.currentStep.auditedTeam === 0 ? this.$t('message.GW_NO_LINKED_TEAM') : this.currentStep.teamName;
    },
    /** Object: Currently focused item */
    currentItem() {
      const { currentStep, focusedCl, focusedClItem } = this;
      return currentStep.checklists[focusedCl] && currentStep.checklists[focusedCl].items[focusedClItem];
    },
    /** Class of the popup displayed when switching to another team */
    auditedTeamPopupClass() {
      const totalLate = this.auditedLatePbs + this.auditedLateActions;
      if (totalLate > 4) {
        return 'red';
      } if (totalLate > 2) {
        return 'orange';
      }
      return '';
    },
    /** Text to display in the popup of the team's data */
    auditedPopupContent() {
      return `
        ${this.makeModalTextLine(this.teamProblems, 'PROBLEMS', this.auditedLatePbs)}
        <br/>
        ${this.makeModalTextLine(this.teamActions.actions, 'ACTIONS', this.auditedLateActions)}
      `;
    },
    auditedLatePbs() {
      return this.auditedTeamLateItems(this.teamProblems);
    },
    auditedLateActions() {
      return this.auditedTeamLateItems(this.teamActions.actions);
    },
    /** End modal: true if all teams msgs are unticked */
    allInfoTeamsUnticked() {
      return !this.teamMessages.some(msg => msg.sendMsg || msg.sendCustomMsg);
    },
    /** Wether this device matches the sm portrait media query */
    isSmLandscape() {
      return this.$store.state.device.isSmartphoneLandscape;
    },
    /** Object: GW instance used for this page */
    compGembawalk() {
      return this.demoMode ? Object.assign({}, IAHelp.demoOngoingGW()) : this.gembaWalk;
    },
    /** Object: Props applied to gw-checklists-wrapper if necesary */
    prevListsAdditionalProps() {
      if (!this.gembaWalk.previousGw || !this.gembaWalk.previousGw.date) {
        return {};
      }
      return { indicatorInitialDate: this.gembaWalk.previousGw.date };
    },
    // Properties used by other components. See src/views/readme.md
    iaTeachText() {
      return this.$t(this.currentTeam.hierarchyLevel > 0
        ? 'sensei.BUILD_MUTUAL_TRUST'
        : 'sensei.TEACHER_TEXT');
    },
    iaTeachBtns() {
      return [this.teacherBtn('NO_BLAME'), this.teacherBtn('FIELD_GOLDEN_RULES'), this.teacherBtn('SPOT_PB')];
    },
    pageTitle() {
      return (this.compGembawalk && this.compGembawalk.name) ? this.compGembawalk.name : this.$t('message.LOADING');
    },
  },

  watch: {
    /** Triggered after App.user loaded */
    defaultDisplay(newVal, oldVal) {
      if (oldVal === null && newVal !== null) {
        this.$nextTick(this.$refs.mainContentSplitter.resetToDefault);
      }
    },
    id() {
      if (this.id !== null) {
        this.refresh();
      } else {
        // Stop timers and reset data
        clearInterval(this.startTimerInterval);
        clearInterval(this.stepTimer);
        Object.assign(this.$data, gwOngoingInitialData());
      }
    },
    stateGembaWalk(newGw, oldGw) {
      this.gembaWalk = cloneDeep(this.stateGembaWalk);
      if (this.gembaWalk && this.gembaWalk.completed === 1) {
        this.stopping = true;
        this.$router.push(`/gw-completed/${this.gembaWalk.id}`);
        return;
      }

      if (!oldGw || (newGw && oldGw.currentStep !== newGw.currentStep)) {
        // Refresh the data of the step
        const step = this.gembaWalk.currentStep;
        if (step <= this.gembaWalk.steps.length && step > 0) {
          this.currentStepIndex = step - 1;
        }
        for (let i = 0, len = this.gembaWalk.steps.length; i < len; i++) {
          this.stepScrollPosition[i] = 0;
        }
        this.refreshStepData(this.currentStepIndex, this.currentStepIndex - 1, true);
        clearInterval(this.startTimerInterval);
        this.startTimerInterval = setInterval(() => {
          // Number of seconds since the beginning
          this.secondsFromStart = this.$moment().diff(this.gembaWalk.date, 's');
        }, 1000);
      }
    },
  },

  created() {
    if (this.id !== null) {
      this.refresh();
    }
    this.registerSocketHandlers();
  },

  beforeDestroy() {
    this.destroyed = true;
    clearInterval(this.startTimerInterval);
    if (this.currentStep.id && !this.stopping) {
      this.$http.post(
        '/server/gembaWalks/setStepDuration.php',
        { id: Number(this.id), stepId: this.currentStep.id, duration: this.currentStep.duration },
      );
    }
  },

  methods: {
    decodeHTML,
    /**
     * Create a string to display as an information for teams audited by the tour
     * @param  {Array} items      Array of problems or actions
     * @param  {String} trSuffix  'PROBLEMS' or 'ACTIONS' to create strings such as
     *                            GW_TEAM_N_PROBLEMS, GW_TEAM_N_ACTIONS, GW_TEAM_1_LATE_PROBLEMS,
     *                            GW_TEAM_1_LATE_ACTIONS, GW_TEAM_N_LATE_PROBLEMS, GW_TEAM_N_LATE_ACTIONS
     * @param  {Number} nLate     Number of late problems/actions
     * @return {String}           Text to display
     */
    makeModalTextLine(items, trSuffix, nLate) {
      let text = '';
      if (items && !isNaN(items.length)) {
        const len = items.length;
        text += this.$tc(`message.GW_TEAM_N_${trSuffix}`, len, { n: len });
        if (len > 0) {
          if (len === 1) {
            text += nLate === 1 ? this.$t(`message.GW_TEAM_1_LATE_${trSuffix}`) : '';
          } else {
            text += this.$tc(`message.GW_TEAM_N_LATE_${trSuffix}`, nLate, { n: nLate });
          }
        }
      }
      return text;
    },
    /** Listen to GW related events */
    registerSocketHandlers() {
      this.handleSocketMessage(SocketMessages.CC_MSGS.GW_STEP, (msg) => {
        if (Number(msg.id) !== Number(this.gembaWalk.id)) return;
        this.refresh();
      });
      this.handleSocketMessage(SocketMessages.CC_MSGS.GW_END, (msg) => {
        if (Number(msg.id) !== Number(this.gembaWalk.id)) return;
        this.stopping = true;
        calert(this.$t('message.GW_OVER'), [
          {
            title: this.$t('message.GW_GOTO_SUMMARY'),
            action: () => {
              this.$router.push(`/gw-completed/${this.gembaWalk.id}`);
            },
          },
          { title: this.$t('message.GW_STAY') },
        ]);
      });
      this.handleSocketMessage(SocketMessages.CC_MSGS.GW_CANCELLED, (msg) => {
        if (Number(msg.id) !== Number(this.gembaWalk.id)) return;
        this.stopping = true;
        calert(this.$t('message.GW_CANCELLED'), [
          {
            title: this.$t('message.GW_PREV_PAGE'),
            action: () => {
              this.$router.back();
            },
          },
        ]);
      });
    },
    textareaAutoSize(evt) {
      const elt = evt.target;
      if (elt) {
        elt.style.height = 'auto';
        elt.style.height = `${elt.scrollHeight + 5}px`;
      }
    },
    refresh() {
      // Timer
      this.secondsFromStart = 0;

      // Fetch the gembaWalk from the server
      this.$store.dispatch(`gembaWalks/${FETCH_GW}`, { id: this.id })
        .catch((err) => {
          if (err && err.data === -1) {
            calert(this.$t('message.GW_NOT_FOUND'), [{
              title: this.$t('message.CANCEL'),
              action: () => {
                this.stopping = true;
                globalEventDispatcher.dispatch(ShizenGlobalEvent.PUSH_HOME_PAGE);
              },
            }]);
          }
        });
    },
    /** Refresh the data of a step */
    refreshStepData(index, prevIndex, noSocketMsg) {
      // Save the scroll position of the checklist in the previous step
      this.removeTeamDataPopup();
      this.currentStepIndex = index;
      this.$nextTick(() => {
        this.stepScrollPosition[prevIndex] = this.$refs.checklistsScroller.scrollTop;
        // Give the steps content div its scroll position
        this.$refs.checklistsScroller.scrollTop = this.stepScrollPosition[index];
        if (this.demoMode) {
          return;
        }
        // Hint provider usage :
        this.App.hintProvider.showHint(this, 10, 200);
        if (this.stepTimer) {
          clearInterval(this.stepTimer);
        }
        // Setup step duration timer
        this.lastStepStart = this.$moment();
        this.lastStepInitialDuration = Number(this.currentStep.duration);
        this.stepTimer = setInterval(() => {
          this.currentStep.duration = this.lastStepInitialDuration
            + Number(this.$moment().diff(this.lastStepStart, 's'));
        }, 500);
        // Reset problems/actions data if necesary
        const prevStep = this.gembaWalk.steps[prevIndex];
        if (!prevStep || prevStep.auditedTeam !== this.currentStep.auditedTeam) {
          this.problemsExpanded = false;
          this.teamProblems = null;
          this.actionsExpanded = false;
          this.teamActions = null;
        }
        if (this.currentStep.id) {
          const params = { id: this.gembaWalk.id, stepId: this.currentStep.id };
          if (prevStep) {
            params.prevStep = { id: prevStep.id, duration: prevStep.duration };
          }
          // Fetch step's related data
          this.$http.post('/server/gembaWalks/getStepData.php', params).then(({ data }) => {
            if (typeof data === 'object') {
              this.teamProblems = data.problems || [];
              this.teamActions = data.actions || [];
              this.currentStep.comments = data.comments || [];
              // Setup users
              this.teamActionUsers = data.users;
              if (!noSocketMsg) {
                this.$store.commit(`gembaWalks/${SET_GW}`, {
                  data: { ...this.stateGembaWalk, currentStep: index + 1 },
                });
                this.sendSocketUpdate();
              }
              this.$nextTick(() => {
                if (this.currentStep.auditedTeam !== 0) {
                  this.createAuditedTeamPopup();
                }
              });
            }
          });
        }
      });
    },
    nextStep() {
      if (this.currentStepIndex < this.compGembawalk.steps.length - 1) {
        this.currentStepIndex++;
        this.refreshStepData(this.currentStepIndex, this.currentStepIndex - 1);
      } else {
        this.setupTeamMessages();
        this.ending = true;
        detectEscapeKey(this.hideEndingBox);
      }
    },
    /** GembaWalker will be able to send a message to audited teams */
    setupTeamMessages() {
      const teams = [];
      const teamIds = this.gembaWalk.steps.map(s => s.auditedTeam)
        .filter((id, idx, self) => id && self.indexOf(id) === idx);
      const name = this.pageTitle;
      const date = this.$moment(this.gembaWalk.date).format(this.$t('dateDayFormat'));
      // Default settings to tick teams by default
      const doSend = this.currentTeam.gwEndTickTeams === 1;
      teamIds.forEach((teamId) => {
        const steps = this.gembaWalk.steps.filter(s => s.auditedTeam === teamId);
        const items = [[], [], []];
        for (let i = 0, len = steps.length; i < len; i++) {
          const step = steps[i];
          // Fetch all items
          for (let j = 0, jlen = step.checklists.length; j < jlen; j++) {
            const checklist = step.checklists[j];
            for (let k = 0, klen = checklist.items.length; k < klen; k++) {
              items[checklist.items[k].ok].push(checklist.items[k]);
            }
          }
        }
        const nNok = items[0].length;
        const nOk = items[1].length;
        const nItems = nNok + nOk;
        // Data
        const teamObj = {
          teamId,
          teamName: steps[0] ? steps[0].teamName : '',
          sendMsg: doSend,
          msg: this.$t('message.GW_TEAM_SUMMARY_INTRO', { name, date }),
          sendCustomMsg: doSend,
          customMsg: '',
        };
        teamObj.msg += this.$tc('message.GW_TEAM_SUMMARY_CHECKS', nItems, { nItems, nOk, nNok });
        for (let j = 0, jlen = items[0].length; j < jlen; j++) {
          teamObj.msg += `<div><span class="ml10 nok-red">● </span>${items[0][j].text}</div>`;
        }
        teams.push(teamObj);
      });
      this.teamMessages = teams;
    },
    /** Un-select all custom and automatic messages sent to teams */
    toggleTeamMsgs() {
      const doSend = this.allInfoTeamsUnticked;
      for (let i = 0, len = this.teamMessages.length; i < len; i++) {
        this.teamMessages[i].sendMsg = doSend;
        this.teamMessages[i].sendCustomMsg = doSend;
      }
    },
    hideEndingBox(usingClick) {
      if (usingClick === true) {
        undetectEscapeKey();
      }
      this.ending = false;
    },
    /** Complete the GW */
    end() {
      const messages = [];
      for (let i = 0, len = this.teamMessages.length, msg, text; i < len; i++) {
        msg = this.teamMessages[i];
        if (msg.sendMsg || msg.sendCustomMsg) {
          text = msg.sendCustomMsg ? msg.customMsg : '';
          messages.push({ team: msg.teamId, message: text });
        }
      }
      this.$http.post('/server/gembaWalks/complete.php', { id: this.gembaWalk.id, messages }).then(({ data }) => {
        if (data === 1) {
          this.hideEndingBox(true);
          globalEventDispatcher.dispatch(ShizenGlobalEvent.GW_COMPLETED);
          this.sendSocketMessage({ socketCompany: 1, id: this.gembaWalk.id, msg: SocketMessages.CC_MSGS.GW_END });
          // Update gw-overlay content
          this.$store.dispatch(`gembaWalks/${FETCH_ONGOING_GW}`);
          this.stopping = true;
          this.$router.push(`/gw-completed/${this.gembaWalk.id}`);
        }
      });
    },
    /** Prompts the user to cancel the field tour */
    cancel() {
      calert(this.$t('message.GW_CANCEL_CONFIRM'), [
        {
          title: this.$t('message.DELETE'),
          action: this.confirmCancel,
          sClass: 'red',
        },
        {
          title: this.$t('message.CANCEL'),
        },
      ]);
    },
    /** Cancel the field tour */
    confirmCancel() {
      this.$http.post('/server/gembaWalks/delete.php', { id: this.gembaWalk.id }).then(({ data }) => {
        if (data === 1) {
          this.sendSocketMessage({ socketCompany: 1, id: this.gembaWalk.id, msg: SocketMessages.CC_MSGS.GW_CANCELLED });
          this.$store.dispatch(`gembaWalks/${FETCH_ONGOING_GW}`);
          this.stopping = true;
          const query = Object.assign({}, { ...this.$route.query, [ongoingGwQueryParam]: undefined });
          this.$router.replace({ query });
        }
      });
    },
    editComments(comments) {
      this.currentStep.comments = comments;
      this.sendSocketUpdate();
    },
    /** Validate / Unvalidate the current step */
    okStep(ok) {
      if (!this.currentStep.id && !(this.gembaWalk.steps || []).length) {
        this.nextStep();
        return;
      }
      this.$store.dispatch(`gembaWalks/${VALIDATE_STEP}`, {
        ok,
        gwId: this.gembaWalk.id,
        id: this.currentStep.id,
        duration: this.currentStep.duration,
      }).then(() => {
        this.nextStep();
        this.sendSocketUpdate();
      });
    },
    alertNoTeam() {
      calert(this.$t('message.GW_NO_TEAM_IN_TEMPLATE'), [{
        title: this.$t('message.CANCEL'),
      }]);
    },
    auditedTeamLateItems(items) {
      let nLate = 0;
      if (items && !isNaN(items.length)) {
        const len = items.length;
        let deadline;
        const now = this.$moment();
        for (let i = 0; i < len; i++) {
          deadline = this.$moment(items[i].deadline);
          if (deadline._isValid && now.isAfter(deadline)) {
            nLate++;
          }
        }
      }
      return nLate;
    },
    createAuditedTeamPopup() {
      // WARNING: Hiding the modal as there's no solution yet as to how implement this with Akiani's design
      return;
      /* eslint-disable no-unreachable */
      const $popup = $($.parseHTML(`<div class="audited-team-popup ${this.auditedTeamPopupClass}">
          <div class="arrow"></div>
          <div class="content"></div>
        </div>`)[0]);
      $popup.find('.content').html(this.auditedPopupContent);
      // popup.style = this.auditedTeamPopupStyle;
      $(document.body).append($popup);
      $(document).on('mousedown touchstart', (e) => {
        if (!$(e.target).closest('.audited-team-popup').length) {
          this.removeTeamDataPopup();
        }
      });
    },
    removeTeamDataPopup() {
      $('.audited-team-popup').remove();
    },
    /** Send a socket msg to notify teams that this problem sheet can be refreshed */
    sendSocketUpdate() {
      this.sendSocketMessage({ socketCompany: 1, id: this.gembaWalk.id, msg: SocketMessages.CC_MSGS.GW_STEP });
    },
    /** Triggered when the focus change inside the checklists wrapper */
    checklistsFocusChange(clIndex, itemIndex, elt) {
      this.focusedCl = clIndex;
      this.focusedClItem = itemIndex;
      this.$nextTick(() => {
        const wrapper = this.$refs.checklistsScroller;
        if (this.demoMode) {
          wrapper.scrollTop = `${elt.offsetTop - 200}px`;
        } else {
          $(wrapper).animate({ scrollTop: `${elt.offsetTop - 200}px` });
        }
      });
    },
    /** Triggered on any checklist edit */
    checklistEdited() {
      this.sendSocketUpdate();
      // Hints
      const cls = this.currentStep.checklists;
      let nOK = 0;
      let nNOK = 0;
      let nNOKHasProblems = 0;
      for (let i = 0, len = cls.length, cl; i < len; i++) {
        cl = cls[i];
        for (let j = 0, jlen = cl.items.length; j < jlen; j++) {
          const item = cl.items[j];
          if (item.ok !== 2) {
            if (item.ok === 0) {
              if (item.problems.length) {
                nNOKHasProblems++;
              }
              nNOK++;
            } else {
              nOK++;
            }
          }
        }
      }
      if (nNOK + nOK > 2 && nNOK > nOK) {
        // Most items are NOK, make sure there are problems
        if (nNOKHasProblems / nNOK < 0.6) {
          this.App.hintProvider.showHint(this, 60, 100, 'gw-ongoing-noks-no-problems');
        } else {
          this.App.hintProvider.showHint(this, 50, 100, 'gw-ongoing-noks');
        }
      }
    },
    createHelpSteps() {
      this.stepIndexBeforeHelp = this.currentStepIndex;
      this.currentStepIndex = 0;
      return [
        new IAHelpStep(null, this.$t('help.GW_ONGOING_INTRO')),
        new IAHelpStep('.gw-ongoing .gw-ongoing__content__steps', this.$t('help.GW_ONGOING_STEPS')),
        new IAHelpStep('.gw-ongoing .gw-ongoing__content__steps .step-item', this.$t('help.GW_ONGOING_STEPS_CLICK')),
        new IAHelpStep('.gw-ongoing .gw-ongoing__content__current', this.$t('help.GW_ONGOING_MAIN')),
        new IAHelpStep('.gw-ongoing .checklist-wrapper', this.$t('help.GW_ONGOING_CHECKLISTS')),
        new IAHelpStep('.gw-ongoing .gw-checklist-item__ok-nok', this.$t('help.GW_ONGOING_CHECKLIST_ITEM'), () => {
          $('.gw-checklist-item__ok-nok__nok').first().trigger('click');
        }),
        new IAHelpStep('.gw-ongoing .gw-checklist-item__buttons__problem', this.$t('help.GW_ONGOING_CHECKLIST_NOK')),
        new IAHelpStep('.gw-ongoing .gw-checklist-item__buttons__comments', this.$t('help.GW_ONGOING_COMMENTS')),
        new IAHelpStep('.gw-ongoing__ok-step', this.$t('help.GW_ONGOING_END_STEP'), () => {
          this.currentStepIndex = this.compGembawalk.steps.length - 1;
          this.nextStep();
        }),
        new IAHelpStep('.gw-ongoing .gw-team-msgs', this.$t('help.GW_ONGOING_END_GW')),
      ];
    },
    helpHidden() {
      this.currentStepIndex = this.stepIndexBeforeHelp;
      if (this.ending) {
        this.hideEndingBox(true);
      }
    },
    gwMessageSend(teamName) {
      return this.$t('message.GW_TEAM_SEND', { teamName });
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";
.gw-ongoing {
  overflow: hidden;

  .users-sync {
    margin-left: 3rem;
  }

  &__content-wrapper.f-h-below-filters {
    padding: 0;
    display: flex;
    flex-flow: column;
  }

  &__content {
    flex: 1;
    min-height: 0px;
    display: flex;
    justify-content: space-between;
    padding: $page-content-padding;
    box-sizing: border-box;

    &.f-h-below-title {
      overflow: visible;
    }

    &__steps {
      overflow: auto;
      flex: 0 0 25%;
      display: flex;
      flex-flow: column;
      height: 100%;
      background: white;
      border-radius: $main-border-radius;
      padding: 1rem;
      box-sizing: border-box;

      h2.basic-sub-title {
        margin-top: 0;
        flex: 0 0 auto;
      }

      .gw-steps-wrapper {
        flex: 1;
      }
    }

    &__current {
      position: relative;
      flex: 0 0 calc(75% - #{$page-content-padding});
      min-width: 0px;
      height: 100%;
      padding-bottom: 1rem;
      box-sizing: border-box;
      display: flex;
      flex-flow: column;
      background: white;
      border-radius: $main-border-radius;

      &__title.basic-sub-title {
        margin: 1rem;
        flex: 0 0 auto;
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
      }

      &__sep {
        margin: .5rem 0 1.5rem 0;
        height: 1px;
        background: $color-gray-background;
      }

      &__wrapper {
        flex: 1;
        padding: 0rem 1rem;
        position: relative;
        display: flex;
        flex-flow: column;
        min-height: 0px;
        overflow: auto;

        &__sep {
          margin: 1.5rem -1rem .5rem -1rem;
          height: 1px;
          background: $color-gray-background;
        }

        > * {
          flex: 0 0 auto;
        }

        .checklist-wrapper {
          margin: 6px 0px;
        }
      }
    }
  }

  &__ok-step {
    flex: 0 0 auto;
    padding: 1.5rem;
    background: white;
    justify-content: flex-end;

    &__btns {
      font-weight: 600;

      > div {
        flex: 0 0 auto;
        margin-left: 1rem;
      }
    }
  }

  .gw-duration {
    margin-right: .5rem;
  }

  .gw-team-data .plan-action .name-deadline > div.name {
    height: 40px;
  }
  .gw-team-msgs.dark-overlay-content {
    padding: 1rem;
    box-sizing: border-box;
    display: flex;
    flex-flow: column;

    h2 {
      margin: 0px;
      flex: 0 0 auto;
    }

    .f-h-below-subtitle {
      flex: 1;
      min-height: 0px;
      position: relative;
      padding: 0px;
      text-align: left;
      box-sizing: border-box;
    }

    .f-h-below-subtitle > div {
      overflow: auto;
      box-sizing: border-box;
      padding: 15px;
    }

    .content-gradient::before {
      top: 0px;
    }

    .content-gradient::after {
      bottom: 0px;
    }

    .main-msg {
      background: #EAEAEA;
      padding: 2px 7px;
      box-sizing: border-box;
    }

    .bordered-field {
      box-sizing: border-box;
    }

    .sep {
      margin: 20px 0px;
      width: 100%;
    }

    .gw-team-msgs__send.generic-border--text-btn {
      flex: 0 0 auto;
      align-self: flex-end;
    }
  }
}
#gw-team-problems, #gw-team-actions {
  height: 0px;
  overflow: hidden;
}

// Modal dynamically added to the body
.audited-team-popup {
  position: fixed;
  top: 108px;
  z-index: 10020;/* Above header */
  right: 40px;
  padding: 10px;
  border-radius: 8px;
  width: 220px;
  font-size: 0.9em;
  font-weight: 600;
  background: #fff;
  box-shadow: 0px 0px 5px #555;
  transform: translateY(-50%);
  border: solid 2px #555;

  &.red {
    border: solid 2px #B71528;
  }
  &.orange {
    border: solid 2px #BD5A0E;
  }
  .arrow {
    position: absolute;
    width: 15px;
    height: 30px;
    overflow: hidden;
    right: 0;
    bottom: 50%;
    transform: translateY(50%) translateX(100%);

    &::after {
      content: "";
      position: absolute;
      left: 0;
      bottom: 50%;
      transform: translateX(-50%) translateY(50%) rotate(45deg);
      width: 15px;
      height: 15px;
      background-color: #555;
      box-shadow: 0px 0px 5px #555;
    }
  }
  &.red .arrow::after {
    background-color: #B71528;
  }
  &.orange .arrow::after {
    background-color: #BD5A0E;
  }
}

// Small screen rules
@media (max-width: $smartphone-landscape) {
  .gw-ongoing {
    overflow: auto;

    &__content {
      flex-flow: column;

      &__steps {
        display: flex;
        align-items: center;
        flex: 0 0 auto;
        height: auto;

        h2 {
          margin-bottom: 0px;
          margin-right: 3px;
        }
      }

      &__current {
        flex: 1;
        min-height: 0px;
      }
    }

    .sm-steps-list {
      overflow: auto;

      > div {
        width: $main-component-height;
        font-weight: 600;
        margin: 5px 3px;
      }
    }
    .dark-overlay .dark-overlay-content.large.gw-team-msgs {
      width: 100%;
      height: 100%;
    }
  }
}

@media (max-width: $smartphone-portrait) {
  .gw-ongoing {
    &__content {
      &__current {
        &__wrapper {
          padding: 1rem;
        }
      }
    }

    &__ok-step {
      padding: .5rem;
      overflow: hidden;

      &__btns {
        > div {
          margin-left: 1rem;

          .icon {
            margin-right: 0;
          }
        }
      }
    }
  }
}
</style>
